// Sortable columns for any table: click a header to sort, click again to flip. Blanks always sort last,
// numbers sort as numbers and words A to Z (with 2 before 10). The server sorts paged lists itself; this is
// for tables that already hold every row.
import {useMemo,useState} from 'react';
import {ArrowDown,ArrowUp,ArrowUpDown} from 'lucide-react';

export type SortDirection='asc'|'desc';
export interface SortState {key:string;direction:SortDirection}

const blank=(v:unknown)=>v===null||v===undefined||v===''||(typeof v==='number'&&Number.isNaN(v));

/** Compares two cell values in ascending order; blanks go last whichever way the column is sorted. */
export function compareCells(a:unknown,b:unknown,direction:SortDirection='asc'):number{
 if(blank(a)||blank(b))return blank(a)&&blank(b)?0:blank(a)?1:-1;
 const sign=direction==='asc'?1:-1;
 if(typeof a==='number'&&typeof b==='number')return (a-b)*sign;
 if(typeof a==='boolean'&&typeof b==='boolean')return (Number(a)-Number(b))*sign;
 const x=String(a),y=String(b);
 // Dates and timestamps compare as text in ISO form; numbers stored as text still compare as numbers.
 return x.localeCompare(y,undefined,{numeric:true,sensitivity:'base'})*sign;
}

/** Rows sorted by one column, with a stable tie-break on their original order. */
export function sortRows<T>(rows:readonly T[],value:(row:T)=>unknown,direction:SortDirection):T[]{
 return rows.map((row,index)=>({row,index,v:value(row)})).sort((a,b)=>compareCells(a.v,b.v,direction)||a.index-b.index).map(x=>x.row);
}

/** Sort state for a table. `columns` maps each sortable column to the value it sorts by. */
export function useSortedRows<T>(rows:readonly T[],columns:Record<string,(row:T)=>unknown>,initial:SortState,firstDirection:Record<string,SortDirection>={}){
 const [sort,setSort]=useState<SortState>(initial);
 const sorted=useMemo(()=>columns[sort.key]?sortRows(rows,columns[sort.key],sort.direction):[...rows],[rows,columns,sort]);
 const toggle=(key:string)=>setSort(s=>s.key===key?{key,direction:s.direction==='asc'?'desc':'asc'}:{key,direction:firstDirection[key]??'asc'});
 return {sorted,sort,toggle,setSort};
}

/** A header cell that sorts its column. Announces the current order to screen readers. */
export function SortHeader({label,column,sort,onSort,className}:{label:string;column:string;sort:SortState;onSort:(column:string)=>void;className?:string}){
 const active=sort.key===column;
 const Icon=active?(sort.direction==='asc'?ArrowUp:ArrowDown):ArrowUpDown;
 return <th className={className} aria-sort={active?(sort.direction==='asc'?'ascending':'descending'):'none'}>
  <button type="button" className={`rnr-sort${active?' is-active':''}`} onClick={()=>onSort(column)} title={`Sort by ${label.toLowerCase()}`}>
   <span>{label}</span><Icon size={13} aria-hidden="true"/>
  </button>
 </th>;
}

/** Keeps rows whose text contains every word of the search, in any column given. */
export function matchesSearch<T>(row:T,query:string,fields:Array<(row:T)=>unknown>):boolean{
 const words=query.toLowerCase().split(/\s+/).filter(Boolean);
 if(!words.length)return true;
 const text=fields.map(f=>{const v=f(row);return v==null?'':String(v);}).join(' ').toLowerCase();
 return words.every(w=>text.includes(w));
}

/** The distinct values of a column, for a filter dropdown, A to Z. */
export function facetValues<T>(rows:readonly T[],value:(row:T)=>unknown):string[]{
 return [...new Set(rows.map(value).filter(v=>!blank(v)).map(String))].sort((a,b)=>a.localeCompare(b,undefined,{numeric:true,sensitivity:'base'}));
}
